import React, { useMemo, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import ViewListIcon from '@mui/icons-material/ViewList'
import { ChipToggleGroup, EmptyState, PulseChip, PulseRow, ToolbarIconButton } from '@/design-system'
import { GalleryGrid, GalleryViewMode } from '../../components/GalleryGrid'
import { AlbumSummary, GallerySummary } from '../../types'
import { formatRelative } from '../../utils/date'
import { useGalleryLibrary } from '../../hooks/useGalleryLibrary'
import { useCreateGallery } from '../../hooks/useCreateGallery'
import { styled } from '@mui/material/styles'

type ShellContext = {
  query: string
  albums: AlbumSummary[]
}

type StatusFilter = 'all' | 'studio' | 'classic' | 'private'

const DOT = { cyan: '#01dafe', coral: '#fea67d', gold: '#ffcf7d' }

const EMPTY_ALBUMS: AlbumSummary[] = []

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'studio', label: 'Studio ◈' },
  { value: 'classic', label: 'Classic ◇' },
  { value: 'private', label: 'Private' },
]

const ViewToggle = styled(Box)({
  display: 'inline-flex',
  gap: 2,
  padding: 3,
  borderRadius: 10,
  background: 'var(--ds-hover)',
  boxShadow: 'inset 0 0 0 1px var(--ds-border)',
  flexShrink: 0,
})

export function GalleryLibraryScreen() {
  const navigate = useNavigate()
  const ctx = useOutletContext<ShellContext | undefined>()
  const { galleries, loading, error, refresh } = useGalleryLibrary()
  const { create: createGallery, isLoading: creating } = useCreateGallery()
  const [status, setStatus] = useState<StatusFilter>('all')
  const [view, setView] = useState<GalleryViewMode>('grid')

  // Client-side join to the shell's already-loaded albums — real titles/covers
  // for the "From album ↗" backlink and the studio-mode placeholder art,
  // with zero extra requests.
  const albums = ctx?.albums ?? EMPTY_ALBUMS
  const sourceAlbumTitles = useMemo(
    () => Object.fromEntries(albums.map((a) => [a.id, a.title])),
    [albums],
  )
  const sourceAlbumCovers = useMemo(
    () => Object.fromEntries(albums.filter((a) => a.coverUrl).map((a) => [a.id, a.coverUrl as string])),
    [albums],
  )

  const filtered = useMemo(() => {
    let list = galleries
    const q = ctx?.query?.trim().toLowerCase()
    if (q) list = list.filter((g) => g.title.toLowerCase().includes(q))
    if (status === 'studio') list = list.filter((g) => g.mode === 'studio')
    if (status === 'classic') list = list.filter((g) => g.mode === 'classic')
    if (status === 'private') list = list.filter((g) => g.privacy === 'private')
    return list
  }, [galleries, ctx?.query, status])

  const totalPrints = useMemo(() => galleries.reduce((sum, g) => sum + (g.printCount || 0), 0), [galleries])
  const classicCount = useMemo(() => galleries.filter((g) => g.mode === 'classic').length, [galleries])
  const buildingCount = useMemo(
    () => galleries.filter((g) => g.sceneLoaded && g.mode === 'studio' && g.arts.length === 0).length,
    [galleries],
  )
  const mostRecent = useMemo(
    () =>
      galleries.reduce<GallerySummary | null>((latest, g) => {
        if (!g.updatedAt) return latest
        if (!latest?.updatedAt || g.updatedAt > latest.updatedAt) return g
        return latest
      }, null),
    [galleries],
  )
  const mostRecentRelative = mostRecent ? formatRelative(mostRecent.updatedAt) : null

  const openStudio = (g: GallerySummary) => navigate(`/galleries/${g.id}/studio`)

  // Option D — instant stub-create, no album/scene picker up front. Which
  // album and which starting scene are asked live inside Studio itself
  // (StudioFirstRunOverlay) once the room's already on screen.
  const createNew = async () => {
    if (creating) return
    try {
      const galleryId = await createGallery()
      navigate(`/galleries/${galleryId}/studio`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not create gallery')
    }
  }

  return (
    <Box className='ds-fade-up'>
      <Box display='flex' alignItems='center' justifyContent='space-between' mb='6px'>
        <Typography component='h1' sx={{ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em' }}>
          Galleries
        </Typography>
        <Box display='flex' alignItems='center' gap={1.5}>
          {!loading && (
            <Typography
              onClick={refresh}
              sx={{
                fontSize: 13,
                color: 'var(--ds-ink-subtle)',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { color: 'var(--ds-ink-muted)' },
              }}
            >
              Refresh
            </Typography>
          )}
          <ViewToggle role='radiogroup' aria-label='View'>
            <ToolbarIconButton
              icon={<ViewModuleIcon sx={{ fontSize: 18 }} />}
              label='Grid view'
              active={view === 'grid'}
              size='small'
              onClick={() => setView('grid')}
            />
            <ToolbarIconButton
              icon={<ViewListIcon sx={{ fontSize: 18 }} />}
              label='List view'
              active={view === 'list'}
              size='small'
              onClick={() => setView('list')}
            />
          </ViewToggle>
        </Box>
      </Box>

      {error && <Typography sx={{ color: 'var(--ds-danger)', mb: 2, fontSize: 14 }}>{error}</Typography>}

      {!loading && galleries.length > 0 && (
        <PulseRow>
          <PulseChip dotColor={DOT.cyan}>
            <b>{totalPrints}</b> prints hung across <b>{galleries.length}</b>{' '}
            {galleries.length === 1 ? 'gallery' : 'galleries'}
          </PulseChip>
          {mostRecent && (
            <PulseChip dotColor={DOT.coral}>
              <b>{mostRecent.title}</b> updated {mostRecentRelative ? <b>{mostRecentRelative}</b> : 'recently'}
            </PulseChip>
          )}
          {buildingCount > 0 ? (
            <PulseChip dotColor={DOT.gold}>
              <b>{buildingCount}</b> {buildingCount === 1 ? 'gallery' : 'galleries'} still hanging prints
            </PulseChip>
          ) : classicCount > 0 ? (
            <PulseChip dotColor={DOT.gold}>
              <b>{classicCount}</b> {classicCount === 1 ? 'room is' : 'rooms are'} Classic-styled
            </PulseChip>
          ) : (
            <PulseChip dotColor={DOT.gold}>Every gallery here is a walkable Studio room</PulseChip>
          )}
        </PulseRow>
      )}

      <Box
        display='flex'
        alignItems='center'
        gap={2}
        flexWrap='wrap'
        sx={{ pt: galleries.length > 0 ? 0 : '12px', pb: '20px' }}
      >
        <ChipToggleGroup options={STATUS_OPTIONS} value={status} onChange={(v) => setStatus(v as StatusFilter)} />
      </Box>

      {!loading && filtered.length === 0 ? (
        <EmptyState
          title={ctx?.query ? 'No matches' : 'No galleries yet'}
          description={
            ctx?.query
              ? 'Try a different search.'
              : 'Create a gallery and land in the room instantly — pick an album and starting scene once you\'re in.'
          }
          actionLabel={ctx?.query ? undefined : creating ? 'Creating…' : 'New gallery'}
          onAction={ctx?.query ? undefined : createNew}
          icon={<ViewInArIcon sx={{ fontSize: 48, color: 'var(--ds-ink-subtle)' }} />}
        />
      ) : (
        <GalleryGrid
          galleries={filtered}
          loading={loading}
          view={view}
          onSelect={openStudio}
          sourceAlbumTitles={sourceAlbumTitles}
          sourceAlbumCovers={sourceAlbumCovers}
          onCreateNew={createNew}
        />
      )}
    </Box>
  )
}
